import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import Stepper from './Stepper';
import PageCard from './PageCard';
import { Case, Recording, isCaseLocked } from '../types';
import { getCase } from '../hooks/cases';
import TaskDisplay from './TaskDisplay';
import AdditionalRecordingCard from './AdditionalRecordingCard';
import { apiFetch } from '../utils/api';

type TranscriptProgress = {
  isProcessing: boolean;
  hasText: boolean;
  hasError?: boolean;
};

const emptyTranscriptProgress: TranscriptProgress = { isProcessing: false, hasText: false };
const noop = () => {};

const hasRecordingText = (recording?: Recording | null): boolean => {
  return Boolean(recording?.text?.trim() || recording?.redacted_transcript?.trim());
};

const progressFromRecording = (recording?: Recording | null): TranscriptProgress => {
  const hasText = hasRecordingText(recording);

  return {
    hasText,
    isProcessing: Boolean(recording?.task_ids?.[0]) && !hasText,
  };
};

const CasePage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const { state } = useLocation();
  const lengthOfRecording = state?.lengthOfRecording as number | undefined;
  const [caseItem, setCaseItem] = useState<Case | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (caseId) {
      getCase(caseId).then(setCaseItem);
    }
  }, [caseId]);
  
  // Track transcript readiness. Continue is blocked only while a transcript is
  // processing, or when the mandatory interview transcript has no text.
  const [interviewProgress, setInterviewProgress] = useState<TranscriptProgress>(emptyTranscriptProgress);
  const [voiceOverProgress, setVoiceOverProgress] = useState<TranscriptProgress>(emptyTranscriptProgress);
  const [isReplacingVoiceOver, setIsReplacingVoiceOver] = useState(false);
  
  // Track extraction status
  const [isExtracting, setIsExtracting] = useState(false);

  useEffect(() => {
    if (!caseItem) return;

    setInterviewProgress(progressFromRecording(caseItem.interview));
    setVoiceOverProgress(
      caseItem.voice_over?.recording_type ? progressFromRecording(caseItem.voice_over) : emptyTranscriptProgress
    );
  }, [caseItem]);

  // Handle when additional recording transcription is completed
  const handleAdditionalTranscriptionComplete = async () => {
    setVoiceOverProgress({ isProcessing: false, hasText: true });
    setIsReplacingVoiceOver(false);
    if (caseId) {
      setCaseItem(await getCase(caseId));
    }
  };

  const replaceMainInterview = async () => {
    if (!caseId) return;

    const response = await apiFetch(
      `/conversation/${caseId}/recordings/conversation/replace`,
      { method: 'POST' }
    );
    if (!response.ok) {
      throw new Error(`Replacement failed: ${response.status}`);
    }
    navigate(`/${caseId}/record`, { state: { caseName: caseItem?.name, replaceExisting: true } });
  };

  const handleIgnoredFailedVoiceOver = async () => {
    setVoiceOverProgress(emptyTranscriptProgress);
    setIsReplacingVoiceOver(false);
    if (caseId) {
      setCaseItem(await getCase(caseId));
    }
  };

  const replaceVoiceOver = async () => {
    if (!caseId) return;

    const response = await apiFetch(
      `/conversation/${caseId}/recordings/voice-over/replace`,
      { method: 'POST' }
    );
    if (!response.ok) {
      throw new Error(`Replacement failed: ${response.status}`);
    }
    setCaseItem(await getCase(caseId));
    setIsReplacingVoiceOver(true);
    setVoiceOverProgress(emptyTranscriptProgress);
  };

  const isLocked = isCaseLocked(caseItem?.status);

  // Determine if "Ga verder" button should be enabled
  const canContinue = () => {
    return !isLocked && interviewProgress.hasText && !interviewProgress.isProcessing && !voiceOverProgress.isProcessing && !voiceOverProgress.hasError;
  };

  const recoveryControlsEnabled = !isLocked;

  // Handle continue - create summary directly with empty themes array
  const handleContinue = async () => {
    if (!caseId) {
      navigate('/');
      return;
    }

    setIsExtracting(true);

    try {
      // Call the extract API with empty themes array
      const response = await apiFetch('/themes/extract', {
        method: 'POST',
        body: JSON.stringify({
          "conversation_id": caseId,
          "selected_themes": [],
          "questionnaire_id": 373
        })
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error('API Error Response:', errorBody);
        throw new Error(`API request failed (${response.status}): ${errorBody}`);
      }

      await response.json();

      navigate(`/case/${caseId}/summary`, { state: { is_new: true } });
    } catch (error) {
      console.error('Error creating summary:', error);
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-primary flex flex-col">
      <Header />

      <PageCard>
        <Stepper step={2} caseId={caseId} isLocked={isLocked} />
        <div className="space-y-8">
          {/* Card 1: Interview Transcription Status */}
          {caseItem?.interview?.recording_type === 'conversation' &&
            <TaskDisplay
              workflowId={caseItem?.interview?.task_ids[0]}
              type="Gesprek"
              lengthOfRecording={lengthOfRecording}
              conversationId={caseId}
              recording={caseItem.interview}
              onLoaded={noop}
              onStatusChange={setInterviewProgress}
              onReplaceRecording={replaceMainInterview}
              recoveryControlsEnabled={recoveryControlsEnabled}
            />
          }

          {/* Card 2: Additional Recording Option - uses same conversation ID from URL */}
          {caseId && (!caseItem?.voice_over?.recording_type || isReplacingVoiceOver) && (
            <AdditionalRecordingCard
              conversationId={caseId}
              onTranscriptionComplete={handleAdditionalTranscriptionComplete}
              onRecordingStarted={noop}
              onTranscriptionStatusChange={setVoiceOverProgress}
              replaceExisting={isReplacingVoiceOver}
              onIgnoreFailedVoiceOver={handleIgnoredFailedVoiceOver}
            />
          )}

          {/* Show additional transcription status if completed */}
          {caseItem?.voice_over?.recording_type === 'voice-over' && !isReplacingVoiceOver && (
            <TaskDisplay
              workflowId={caseItem?.voice_over?.task_ids[0]}
              type="Persoonlijke opmerkingen"
              conversationId={caseId}
              recording={caseItem.voice_over}
              onLoaded={noop}
              onStatusChange={setVoiceOverProgress}
              onReplaceRecording={replaceVoiceOver}
              onIgnoreFailedRecording={handleIgnoredFailedVoiceOver}
              recoveryControlsEnabled={recoveryControlsEnabled}
            />
          )}

          {/* Continue Button */}
          <div className="text-center pt-4">
            <button
              onClick={handleContinue}
              disabled={!canContinue() || isExtracting}
              title={!canContinue() ? 'Wacht tot de verwerking voltooid is' : ''}
              className="px-8 py-3 bg-brand-blauw hover:bg-brand-blauw/90 text-brand-beige rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExtracting ? 'Bezig met verwerken...' : 'Ga verder met de analyse →'}
            </button>
          </div>
        </div>
      </PageCard>

      <Footer />
    </div>
  );
};

export default CasePage; 